import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Footer } from './components/Footer';
import { WhatsAppFloat } from './components/WhatsAppFloat';
import { translations } from './translations';
import { seoPages, allSeoSlugs } from './data/seoPages';

import { CookieBanner } from './components/CookieBanner';

// Lazy load components below the fold for better initial load performance
const About = React.lazy(() => import('./components/About').then(m => ({ default: m.About })));
const ServicesNew = React.lazy(() => import('./components/ServicesNew').then(m => ({ default: m.ServicesNew })));
const USP = React.lazy(() => import('./components/USP').then(m => ({ default: m.USP })));
const Testimonials = React.lazy(() => import('./components/Testimonials').then(m => ({ default: m.Testimonials })));
const Blog = React.lazy(() => import('./components/Blog').then(m => ({ default: m.Blog })));
const Contact = React.lazy(() => import('./components/Contact').then(m => ({ default: m.Contact })));
const Privacy = React.lazy(() => import('./components/Privacy').then(m => ({ default: m.Privacy })));
const Impressum = React.lazy(() => import('./components/Impressum').then(m => ({ default: m.Impressum })));
const ForWhom = React.lazy(() => import('./components/ForWhom').then(m => ({ default: m.ForWhom })));
const DigitalProducts = React.lazy(() => import('./components/DigitalProducts').then(m => ({ default: m.DigitalProducts })));
const GiftBoxes = React.lazy(() => import('./components/GiftBoxes').then(m => ({ default: m.GiftBoxes })));
const Process = React.lazy(() => import('./components/Process').then(m => ({ default: m.Process })));
const FAQ = React.lazy(() => import('./components/FAQ').then(m => ({ default: m.FAQ })));
const ServiceArea = React.lazy(() => import('./components/ServiceArea').then(m => ({ default: m.ServiceArea })));
const SeniorCare = React.lazy(() => import('./components/SeniorCare').then(m => ({ default: m.SeniorCare })));
const ServicesDirectory = React.lazy(() => import('./components/ServicesDirectory').then(m => ({ default: m.ServicesDirectory })));
const SeoPage = React.lazy(() => import('./components/SeoPage').then(m => ({ default: m.SeoPage })));

function AppContent() {
  const [currentLang, setCurrentLang] = React.useState<'de' | 'en' | 'es'>(() => {
    const savedLang = localStorage.getItem('geis-care-lang');
    return (savedLang === 'de' || savedLang === 'en' || savedLang === 'es') ? savedLang : 'de';
  });

  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    localStorage.setItem('geis-care-lang', currentLang);
    // Update meta tags
    if (location.pathname === '/') {
      const meta = translations[currentLang].meta;
      document.title = meta.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', meta.description);
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute('content', meta.keywords);
    }
    document.documentElement.lang = currentLang;
  }, [currentLang, location.pathname]);

  const goHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-primary/20 font-sans text-text antialiased selection:bg-accent/30 selection:text-headline">
      <Routes>
        {/* Home Page */}
        <Route path="/" element={
          <>
            <Header currentLang={currentLang} onLanguageChange={setCurrentLang} />
            <main>
              <Hero currentLang={currentLang} />
              <React.Suspense fallback={<div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <About currentLang={currentLang} />
                <USP currentLang={currentLang} />
                <Process currentLang={currentLang} />
                <SeniorCare currentLang={currentLang} />
                <ServicesDirectory currentLang={currentLang} />
                <ServicesNew currentLang={currentLang} />
                <ForWhom currentLang={currentLang} />
                {/* <DigitalProducts currentLang={currentLang} /> */}
                <GiftBoxes currentLang={currentLang} />
                <Testimonials currentLang={currentLang} />
                <ServiceArea currentLang={currentLang} />
                <FAQ currentLang={currentLang} />
                <Blog currentLang={currentLang} />
                <Contact currentLang={currentLang} />
              </React.Suspense>
            </main>
            <Footer currentLang={currentLang} />
            <CookieBanner currentLang={currentLang} />
            <WhatsAppFloat currentLang={currentLang} />
          </>
        } />

        {/* Privacy Page */}
        <Route path="/datenschutz" element={
          <>
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
              <Privacy currentLang={currentLang} onLanguageChange={setCurrentLang} />
            </React.Suspense>
            <Footer currentLang={currentLang} />
            <WhatsAppFloat currentLang={currentLang} />
          </>
        } />

        {/* Impressum Page */}
        <Route path="/impressum" element={
          <>
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
              <Impressum currentLang={currentLang} onLanguageChange={setCurrentLang} />
            </React.Suspense>
            <Footer currentLang={currentLang} />
            <WhatsAppFloat currentLang={currentLang} />
          </>
        } />

        {/* Service & Location SEO Pages */}
        {allSeoSlugs.filter(slug => !slug.includes('/')).map(slug => (
          <Route key={slug} path={`/${slug}`} element={
            <>
              <Header currentLang={currentLang} onLanguageChange={setCurrentLang} />
              <div className="pt-16">
                <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                  <SeoPage page={seoPages[slug]} onBack={goHome} currentLang={currentLang} />
                </React.Suspense>
              </div>
              <Footer currentLang={currentLang} />
              <WhatsAppFloat currentLang={currentLang} />
            </>
          } />
        ))}

        {/* Guide Pages */}
        <Route path="/guide/:slug" element={
          <>
            <Header currentLang={currentLang} onLanguageChange={setCurrentLang} />
            <div className="pt-16">
              <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <GuidePageWrapper currentLang={currentLang} onBack={goHome} />
              </React.Suspense>
            </div>
            <Footer currentLang={currentLang} />
            <WhatsAppFloat currentLang={currentLang} />
          </>
        } />

        {/* Ratgeber Pages */}
        <Route path="/ratgeber/:slug" element={
          <>
            <Header currentLang={currentLang} onLanguageChange={setCurrentLang} />
            <div className="pt-16">
              <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <RatgeberPageWrapper currentLang={currentLang} onBack={goHome} />
              </React.Suspense>
            </div>
            <Footer currentLang={currentLang} />
            <WhatsAppFloat currentLang={currentLang} />
          </>
        } />
      </Routes>
    </div>
  );
}

// Wrapper for Guide Pages
function GuidePageWrapper({ currentLang, onBack }: { currentLang: 'de' | 'en' | 'es'; onBack: () => void }) {
  const location = useLocation();
  const slug = location.pathname.split('/').pop() || '';
  const fullSlug = `guide/${slug}`;
  const pageData = seoPages[fullSlug];
  
  if (!pageData) {
    return <div className="container mx-auto px-6 py-20 text-center">Seite nicht gefunden</div>;
  }
  
  return <SeoPage page={pageData} onBack={onBack} currentLang={currentLang} />;
}

// Wrapper for Ratgeber Pages
function RatgeberPageWrapper({ currentLang, onBack }: { currentLang: 'de' | 'en' | 'es'; onBack: () => void }) {
  const location = useLocation();
  const slug = location.pathname.split('/').pop() || '';
  const fullSlug = `ratgeber/${slug}`;
  const pageData = seoPages[fullSlug];
  
  if (!pageData) {
    return <div className="container mx-auto px-6 py-20 text-center">Seite nicht gefunden</div>;
  }
  
  return <SeoPage page={pageData} onBack={onBack} currentLang={currentLang} />;
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
